import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';

import {
  AppMessagesConfig,
  AppsConfig,
  LoginModuleConfig,
  ResolvedAppConfig,
  StatesMapResult,
} from '../types/login-config.types';
import { buildStatesMap } from '../utils/build-states-map.util';
import { resolveAppUrlFromPartialAppUrl } from '../utils/resolve-app-url.util';

const EMPTY_LOGIN_MODULE: LoginModuleConfig = {
  partialAppUrl: '/u/login/',
};

const EMPTY_RESOLVED_APP_CONFIG: ResolvedAppConfig = {
  loginModule: EMPTY_LOGIN_MODULE,
  xMcAppId: '',
  stateKey: '',
};

@Injectable()
export class ConfigService {
  private statesMap: StatesMapResult = {
    states: {},
    global: null,
    defaultStateKey: undefined,
  };

  private _grid = 'unknown';
  private _appsConfig: AppsConfig = {};
  private _appMessagesConfig: AppMessagesConfig = {};
  private _initialUrlParams = new URLSearchParams();

  private readonly _configLoaded = new ReplaySubject<void>(1);
  readonly configLoaded$: Observable<void> = this._configLoaded.asObservable();

  get grid(): string {
    return this._grid;
  }

  get appsConfig(): AppsConfig {
    return this._appsConfig;
  }

  get appMessagesConfig(): AppMessagesConfig {
    return this._appMessagesConfig;
  }

  get initialUrlParams(): URLSearchParams {
    return this._initialUrlParams;
  }

  private loadFromConfig(
    config: {
      grid: string;
      appsConfig: AppsConfig;
      appMessagesConfig: AppMessagesConfig;
    },
    initialUrlParams: URLSearchParams
  ): void {
    this._grid = config.grid;
    this._appsConfig = config.appsConfig;
    this._appMessagesConfig = config.appMessagesConfig;
    this._initialUrlParams = initialUrlParams;
    this.statesMap = buildStatesMap(config.appsConfig);
    this._configLoaded.next();
  }

  /** @internal Application bootstrap only; use provideLoginConfig(). */
  static applyBootstrapConfig(
    service: ConfigService,
    config: {
      grid: string;
      appsConfig: AppsConfig;
      appMessagesConfig: AppMessagesConfig;
    },
    initialUrlParams: URLSearchParams
  ): void {
    service.loadFromConfig(config, initialUrlParams);
  }

  resolveAppConfig(gta?: string): ResolvedAppConfig {
    const effectiveGta = this.effectiveGta(gta);
    const { states, global } = this.statesMap;
    const fallback = global ?? EMPTY_RESOLVED_APP_CONFIG;

    if (effectiveGta && states[effectiveGta]) {
      return states[effectiveGta];
    }

    return fallback;
  }

  /**
   * True when appsConfig produced at least one resolvable login-module entry.
   * Empty or unusable config leaves the login form unable to complete.
   */
  hasResolvableAppConfig(): boolean {
    const { states, global } = this.statesMap;
    return global !== null || Object.keys(states).length > 0;
  }

  resolveMessageKey(gta?: string): string | undefined {
    const effectiveGta = this.effectiveGta(gta);
    return effectiveGta || this.statesMap.defaultStateKey;
  }

  resolveAppUrl(gta?: string): string {
    const { loginModule } = this.resolveAppConfig(gta);
    const partialAppUrl = loginModule.partialAppUrl;

    if (!partialAppUrl) {
      return '/u/login/';
    }

    return resolveAppUrlFromPartialAppUrl(partialAppUrl);
  }

  private effectiveGta(gta?: string): string {
    if (gta !== undefined) {
      return gta;
    }

    return this._initialUrlParams.get('gta') ?? '';
  }
}
